// Buttons of the whole system (DESIGN.md «Контролы»): one button with variants and three sizes, the icon-only button with its
// tooltip, the key hint. Other controls build on these.

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Icon } from './icons.tsx';
import { useTooltip } from './Tooltip.tsx';
import s from './ui.module.css';
import c from './controls.module.css';

export const cx = (...v: (string | false | null | undefined)[]) => v.filter(Boolean).join(' ');

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'ghost' | 'secondary' | 'primary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: string;
  iconOnly?: boolean;
  pressed?: boolean;
  /** A trigger of a menu or a list: the down arrow after the text turns up while it is open. */
  caret?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = 'ghost', size = 'md', icon, iconOnly, pressed, caret, className, children, ...rest }, ref,
) {
  return (
    <button ref={ref} type="button" {...rest}
      className={cx(s.btn, variant !== 'ghost' && s[variant], size !== 'md' && s[size], iconOnly && s.icon, pressed && s.pressed, className)}>
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      {children}
      {caret && !iconOnly && <span className={c.chevron} data-dir="down" aria-hidden="true"><Icon name="chevronDown" size={12} /></span>}
    </button>
  );
});

/**
 * A button with only an icon: its name is the screen reader's label and the tooltip, never the browser's own `title`.
 * `hint` — a longer tooltip (with the key: «В архив (E)»). One element, so it can be the trigger of a menu.
 */
export const IconButton = forwardRef<HTMLButtonElement, Omit<BtnProps, 'iconOnly' | 'children' | 'caret'> & {
  icon: string; label: string; hint?: string; tip?: 'bottom' | 'right' | false;
}>(function IconButton({ icon, label, hint, tip = 'bottom', onMouseEnter, onMouseLeave, onFocus, onBlur, ...rest }, ref) {
  const { handlers, node } = useTooltip(hint ?? label, tip || 'bottom', { onMouseEnter, onMouseLeave, onFocus, onBlur });
  return (
    <Button ref={ref} {...rest} {...(tip ? handlers : { onMouseEnter, onMouseLeave, onFocus, onBlur })} icon={icon} iconOnly aria-label={label}>
      {tip && node}
    </Button>
  );
});

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export function Kbd({ keys }: { keys: string[] }) {
  return <span className={s.shortcut}>{keys.map((k) => <kbd key={k} className={s.kbd}>{k === 'mod' ? (isMac ? '⌘' : 'Ctrl') : k}</kbd>)}</span>;
}
